import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ClientDashboardTabs from '../../components/navigation/ClientDashboardTabs';
import ColorCard from './components/ColorCard';
import ColorFormModal from './components/ColorFormModal';
import BulkImportModal from './components/BulkImportModal';
import ColorFilters from './components/ColorFilters';
import ColorStats from './components/ColorStats';

const companyService = {
  getBySlug: async (slug) => {
    // Mock implementation - replace with actual API call
    return { slug, name: 'Company Name' };
  }
};

const ColorsManagement = () => {
  const navigate = useNavigate();
  const { companyIdentifier } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [colors, setColors] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    group: '',
    sortBy: 'name-asc'
  });

  useEffect(() => {
    // If accessed via company-specific route, load company data
    if (companyIdentifier) {
      const loadCompanyData = async () => {
        try {
          setLoading(true);
          // Extract slug from compound identifier
          const extractSlugFromIdentifier = (identifier) => {
            const parts = identifier?.split('-');
            let uuidStartIndex = -1;
            for (let i = parts?.length - 1; i >= 0; i--) {
              if (parts?.[i]?.length === 12 && /^[a-f0-9]+$/i?.test(parts?.[i])) {
                if (i >= 4 &&
                parts?.[i - 1]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 1]) &&
                parts?.[i - 2]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 2]) &&
                parts?.[i - 3]?.length === 4 && /^[a-f0-9]+$/i?.test(parts?.[i - 3]) &&
                parts?.[i - 4]?.length === 8 && /^[a-f0-9]+$/i?.test(parts?.[i - 4])) {
                  uuidStartIndex = i - 4;
                  break;
                }
              }
            }
            if (uuidStartIndex > 0) {
              return parts?.slice(0, uuidStartIndex)?.join('-');
            }
            return identifier;
          };

          const slug = extractSlugFromIdentifier(companyIdentifier);
          const normalizedSlug = decodeURIComponent(slug || '')?.toLowerCase()?.trim();

          const companyData = await companyService?.getBySlug(normalizedSlug);
          setCompany(companyData);
        } catch (error) {
          console.error('Error loading company data:', error);
        } finally {
          setLoading(false);
        }
      };

      loadCompanyData();
    } else {
      setLoading(false);
    }
  }, [companyIdentifier]);

  useEffect(() => {
    const mockColors = [
    {
      id: 1,
      name: 'Ocean Blue',
      hexCode: '#0EA5E9',
      group: 'base-colors',
      usageCount: 45,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_15ab79267-1765763745028.png",
      swatchImageAlt: 'Smooth ocean blue epoxy floor texture with glossy finish in modern interior space',
      createdAt: '2024-11-15T10:30:00Z'
    },
    {
      id: 2,
      name: 'Charcoal Gray',
      hexCode: '#374151',
      group: 'base-colors',
      usageCount: 38,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
      swatchImageAlt: 'Dark charcoal gray epoxy coating with subtle metallic flakes on industrial concrete floor',
      createdAt: '2024-11-10T14:20:00Z'
    },
    {
      id: 3,
      name: 'Sunset Orange',
      hexCode: '#F97316',
      group: 'popular',
      usageCount: 52,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_193ee2927-1764798924046.png",
      swatchImageAlt: 'Vibrant sunset orange epoxy floor with warm undertones in residential garage setting',
      createdAt: '2024-10-28T09:15:00Z'
    },
    {
      id: 4,
      name: 'Forest Green',
      hexCode: '#10B981',
      group: 'seasonal',
      usageCount: 29,
      swatchImage: null,
      swatchImageAlt: '',
      createdAt: '2024-11-20T16:45:00Z'
    },
    {
      id: 5,
      name: 'Midnight Black',
      hexCode: '#1E293B',
      group: 'base-colors',
      usageCount: 61,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
      swatchImageAlt: 'Deep midnight black epoxy floor with high gloss reflective surface in commercial showroom',
      createdAt: '2024-10-05T11:00:00Z'
    },
    {
      id: 6,
      name: 'Silver Metallic',
      hexCode: '#94A3B8',
      group: 'metallic',
      usageCount: 34,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe49e850-1765763743651.png",
      swatchImageAlt: 'Shimmering silver metallic epoxy with pearl effect on workshop floor under bright lighting',
      createdAt: '2024-11-12T13:30:00Z'
    },
    {
      id: 7,
      name: 'Ruby Red',
      hexCode: '#DC2626',
      group: 'flake-colors',
      usageCount: 27,
      swatchImage: null,
      swatchImageAlt: '',
      createdAt: '2024-11-18T08:20:00Z'
    },
    {
      id: 8,
      name: 'Cream White',
      hexCode: '#F5F5F4',
      group: 'base-colors',
      usageCount: 41,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18dbdb1c2-1764658221579.png",
      swatchImageAlt: 'Clean cream white epoxy floor with subtle texture in bright modern kitchen space',
      createdAt: '2024-10-22T15:10:00Z'
    },
    {
      id: 9,
      name: 'Copper Bronze',
      hexCode: '#B45309',
      group: 'metallic',
      usageCount: 19,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1fde8d284-1766029957550.png",
      swatchImageAlt: 'Rich copper bronze metallic epoxy with swirled pattern on luxury basement floor',
      createdAt: '2024-11-25T10:05:00Z'
    },
    {
      id: 10,
      name: 'Sky Blue',
      hexCode: '#38BDF8',
      group: 'popular',
      usageCount: 36,
      swatchImage: null,
      swatchImageAlt: '',
      createdAt: '2024-11-08T12:40:00Z'
    },
    {
      id: 11,
      name: 'Emerald Green',
      hexCode: '#059669',
      group: 'flake-colors',
      usageCount: 23,
      swatchImage: "https://img.rocket.new/generatedImages/rocket_gen_img_15ab79267-1765763745028.png",
      swatchImageAlt: 'Vibrant emerald green epoxy with decorative flakes scattered across retail store floor',
      createdAt: '2024-10-30T14:55:00Z'
    },
    {
      id: 12,
      name: 'Sandstone Beige',
      hexCode: '#D6D3D1',
      group: 'custom',
      usageCount: 15,
      swatchImage: null,
      swatchImageAlt: '',
      createdAt: '2024-12-01T09:30:00Z'
    }];


    setColors(mockColors);
    setFilteredColors(mockColors);
  }, []);

  useEffect(() => {
    let result = [...colors];

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter(
        (color) =>
        color?.name?.toLowerCase()?.includes(searchLower) ||
        color?.hexCode?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.group) {
      result = result?.filter((color) => color?.group === filters?.group);
    }

    const [sortField, sortDirection] = filters?.sortBy?.split('-');
    result?.sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'name':
          aVal = a?.name?.toLowerCase();
          bVal = b?.name?.toLowerCase();
          break;
        case 'usage':
          aVal = a?.usageCount;
          bVal = b?.usageCount;
          break;
        case 'date':
          aVal = new Date(a.createdAt);
          bVal = new Date(b.createdAt);
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredColors(result);
  }, [colors, filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      group: '',
      sortBy: 'name-asc'
    });
  };

  const handleAddColor = () => {
    setEditingColor(null);
    setIsFormModalOpen(true);
  };

  const handleEditColor = (color) => {
    setEditingColor(color);
    setIsFormModalOpen(true);
  };

  const handleSaveColor = (colorData) => {
    if (editingColor) {
      setColors((prev) =>
      prev?.map((color) => color?.id === colorData?.id ? colorData : color)
      );
    } else {
      setColors((prev) => [...prev, colorData]);
    }
  };

  const handleDeleteColor = (colorId) => {
    setColors((prev) => prev?.filter((color) => color?.id !== colorId));
  };

  const handleBulkImport = (importedColors) => {
    setColors((prev) => [...prev, ...importedColors]);
  };

  return (
    <div className="min-h-screen bg-background">
      {companyIdentifier && <ClientDashboardTabs />}
      <div className={companyIdentifier ? "main-content-with-tabs" : ""}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">
                Colors Management
              </h1>
              <p className="text-text-secondary">
                Manage your color palette for customer visualizer selections
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setIsBulkImportOpen(true)}
                iconName="Upload"
                iconPosition="left"
                iconSize={16}>

                Bulk Import
              </Button>
              <Button
                variant="default"
                onClick={handleAddColor}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}>

                Add Color
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <ColorStats colors={colors} />

            <ColorFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters} />


            {filteredColors?.length === 0 ?
            <div className="bg-card border border-border rounded-lg p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Palette" size={32} className="text-text-secondary" />
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  No colors found
                </h3>
                <p className="text-text-secondary mb-6">
                  {filters?.search || filters?.group ?
                'Try adjusting your filters or search terms' : 'Get started by adding your first color'}
                </p>
                {!filters?.search && !filters?.group &&
              <Button
                variant="default"
                onClick={handleAddColor}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}>

                    Add Your First Color
                  </Button>
              }
              </div> :

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredColors?.map((color) =>
              <ColorCard
                key={color?.id}
                color={color}
                onEdit={handleEditColor}
                onDelete={handleDeleteColor} />

              )}
              </div>
            }
          </div>
        </div>
      </div>
      <ColorFormModal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingColor(null);
        }}
        onSave={handleSaveColor}
        editColor={editingColor} />

      <BulkImportModal
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onImport={handleBulkImport} />

    </div>);

};

export default ColorsManagement;